import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import SEO from '../../components/SEO';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumb from '../../components/Breadcrumb';
import InternalLinks from '../../components/InternalLinks';
import { 
  ArrowRight,
  Clock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Lightbulb,
  Target,
  BarChart3,
  Scale,
  Euro,
  Users,
  Leaf,
  Award,
  Briefcase,
  TrendingUp
} from 'lucide-react';

export default function CriteresAttributionArticle() {
  return (
    <>
      <SEO 
        title="Critères d'Attribution des Marchés Publics | Guide Complet 2026"
        description="Comprendre les critères d'attribution des marchés publics : prix, valeur technique, délai, environnement. Comment sont notées les offres ? Guide expert pour optimiser vos réponses."
        path="/blog/criteres-attribution-marches-publics"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-slate-50 border-b">
          <div className="container mx-auto px-4 py-3">
            <Breadcrumb 
              items={[
                { label: 'Blog', href: '/blog' },
                { label: 'Critères d\'attribution' }
              ]}
            />
          </div>
        </div>

        {/* Article Header */}
        <article>
          <header className="bg-gradient-to-b from-violet-50 to-white py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                  Guide Expert
                </span>
                <span className="flex items-center gap-1 text-slate-500 text-sm">
                  <Calendar className="w-4 h-4" />
                  12 mars 2026
                </span>
                <span className="flex items-center gap-1 text-slate-500 text-sm">
                  <Clock className="w-4 h-4" />
                  15 min de lecture
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Critères d'Attribution des Marchés Publics
              </h1>
              
              <p className="text-xl text-slate-600">
                Comment les acheteurs publics évaluent-ils les offres ? Quels critères sont les plus importants ? Ce guide vous explique tout pour optimiser vos chances de remporter un marché.
              </p>
            </div>
          </header>

          {/* Article Content */}
          <div className="container mx-auto px-4 max-w-4xl py-12">
            <div className="prose prose-lg max-w-none">
              
              <p>
                Les critères d'attribution sont les éléments sur lesquels l'acheteur public se base pour choisir l'offre la plus avantageuse. Comprendre ces critères est essentiel pour construire une <Link to="/blog/comment-repondre-appel-offres" className="text-violet-600 hover:text-violet-700 font-medium">réponse efficace à un appel d'offres</Link>.
              </p>

              <p>
                Une bonne <Link to="/veille-marches-publics" className="text-violet-600 hover:text-violet-700 font-medium">veille marchés publics</Link> vous permet d'identifier rapidement les opportunités correspondant à vos forces. Notre <Link to="/plateforme-appels-offres" className="text-violet-600 hover:text-violet-700 font-medium">plateforme d'appels d'offres</Link> facilite ensuite l'<Link to="/analyse-dce" className="text-violet-600 hover:text-violet-700 font-medium">analyse des DCE</Link> pour repérer les critères et leur pondération.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Le principe de l'offre économiquement la plus avantageuse
              </h2>

              <p>
                Depuis la réforme de 2016, les marchés publics sont attribués à l'<strong>offre économiquement la plus avantageuse (OEPA)</strong>. Cela signifie que le prix n'est pas le seul critère : d'autres éléments peuvent être pris en compte.
              </p>

              <Card className="my-8 border-blue-200 bg-blue-50">
                <CardContent className="p-6">
                  <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Article R2152-7 du Code de la commande publique
                  </h4>
                  <p className="text-blue-900 italic">
                    "L'offre économiquement la plus avantageuse est déterminée sur la base du prix ou du coût, selon une approche fondée sur le rapport coût/efficacité [...]"
                  </p>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Les principaux critères d'attribution
              </h2>

              <p>
                Les critères les plus fréquemment utilisés dans les marchés publics sont :
              </p>

              {/* Criteria Cards */}
              <div className="grid md:grid-cols-2 gap-6 my-8">
                <Card className="border-2 border-violet-200 hover:border-violet-400 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                        <Euro className="w-6 h-6 text-violet-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">Prix</h3>
                        <span className="text-sm text-violet-600">Pondération : 30% à 70%</span>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm">
                      Le critère prix peut être évalué sur le montant total, les prix unitaires ou le coût global (TCO).
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-orange-200 hover:border-orange-400 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <Target className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">Valeur technique</h3>
                        <span className="text-sm text-orange-600">Pondération : 30% à 60%</span>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm">
                      Évalue la qualité de la solution proposée : méthodologie, moyens, organisation, expertise.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-green-200 hover:border-green-400 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">Délai d'exécution</h3>
                        <span className="text-sm text-green-600">Pondération : 5% à 20%</span>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm">
                      Capacité à respecter ou améliorer les délais demandés par l'acheteur.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-emerald-200 hover:border-emerald-400 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <Leaf className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">Environnement</h3>
                        <span className="text-sm text-emerald-600">Pondération : 5% à 20%</span>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm">
                      Critère obligatoire depuis 2026 : impact environnemental, RSE, bilan carbone.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Comment sont notées les offres ?
              </h2>

              <p>
                Chaque critère est noté sur une échelle (généralement 0 à 10 ou 0 à 20), puis pondéré selon son importance relative.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">
                Exemple de notation
              </h3>

              <Card className="my-6 border-slate-200">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-600 mb-4">Marché de prestations informatiques - Pondération des critères :</p>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Prix (40%)</span>
                        <span className="text-sm text-slate-500">16/20</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div className="bg-violet-600 h-2 rounded-full" style={{width: '80%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Valeur technique (45%)</span>
                        <span className="text-sm text-slate-500">14/20</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{width: '70%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Délai (10%)</span>
                        <span className="text-sm text-slate-500">18/20</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: '90%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Environnement (5%)</span>
                        <span className="text-sm text-slate-500">12/20</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div className="bg-emerald-500 h-2 rounded-full" style={{width: '60%'}}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-900">Note finale pondérée</span>
                      <span className="text-xl font-bold text-violet-600">14.9/20</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Calcul : (16×0.40) + (14×0.45) + (18×0.10) + (12×0.05) = 14.9
                    </p>
                  </div>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Sous-critères et grilles de notation
              </h2>

              <p>
                Les critères principaux peuvent être déclinés en sous-critères. Voici un exemple type pour la valeur technique :
              </p>

              <div className="overflow-x-auto my-8">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-violet-50">
                      <th className="border border-slate-300 px-4 py-2 text-left">Sous-critère</th>
                      <th className="border border-slate-300 px-4 py-2 text-center">Pondération</th>
                      <th className="border border-slate-300 px-4 py-2 text-left">Ce qui est évalué</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-300 px-4 py-2 font-medium">Méthodologie</td>
                      <td className="border border-slate-300 px-4 py-2 text-center">15%</td>
                      <td className="border border-slate-300 px-4 py-2">Approche proposée, phasage, processus</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-2 font-medium">Équipe projet</td>
                      <td className="border border-slate-300 px-4 py-2 text-center">10%</td>
                      <td className="border border-slate-300 px-4 py-2">CV, expérience, disponibilité</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-2 font-medium">Références</td>
                      <td className="border border-slate-300 px-4 py-2 text-center">10%</td>
                      <td className="border border-slate-300 px-4 py-2">Projets similaires réalisés</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-2 font-medium">Planning</td>
                      <td className="border border-slate-300 px-4 py-2 text-center">10%</td>
                      <td className="border border-slate-300 px-4 py-2">Réalisme, détail, jalons</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <Card className="my-8 border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Conseil stratégique
                  </h4>
                  <p className="text-amber-900">
                    Avant de rédiger votre offre, analysez la pondération des critères dans le <Link to="/blog/rc-vs-cctp-vs-ccap" className="text-amber-700 underline font-medium">RC</Link>. Si le prix compte pour 70%, ne passez pas des heures sur votre mémoire technique. Inversement, si la valeur technique est à 60%, c'est là que vous devez concentrer vos efforts.
                  </p>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Le critère environnemental : obligation depuis 2026
              </h2>

              <p>
                Depuis le 21 août 2026, le Code de la commande publique impose la prise en compte d'au moins un critère environnemental dans les marchés publics.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">
                Exemples de critères environnementaux
              </h3>

              <ul className="space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <Leaf className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
                  <span><strong>Bilan carbone</strong> : émissions CO2 du produit ou de la prestation</span>
                </li>
                <li className="flex items-start gap-3">
                  <Leaf className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
                  <span><strong>Économie circulaire</strong> : matériaux recyclés, recyclabilité</span>
                </li>
                <li className="flex items-start gap-3">
                  <Leaf className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
                  <span><strong>Circuit court</strong> : proximité géographique des fournisseurs</span>
                </li>
                <li className="flex items-start gap-3">
                  <Leaf className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
                  <span><strong>Labels environnementaux</strong> : Écolabel, ISO 14001...</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Optimiser sa réponse selon les critères
              </h2>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      À faire
                    </h4>
                    <ul className="space-y-2 text-sm text-green-900">
                      <li>✓ Analyser la pondération avant de rédiger</li>
                      <li>✓ Répondre point par point aux sous-critères</li>
                      <li>✓ Utiliser des preuves concrètes (références, CV)</li>
                      <li>✓ Soigner la présentation du mémoire technique</li>
                      <li>✓ Quantifier vos engagements (délais, garanties)</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      À éviter
                    </h4>
                    <ul className="space-y-2 text-sm text-red-900">
                      <li>✗ Réponses génériques non personnalisées</li>
                      <li>✗ Ignorer certains sous-critères</li>
                      <li>✗ Sur-promettre sans justification</li>
                      <li>✗ Prix anormalement bas sans explication</li>
                      <li>✗ Documents mal présentés ou incomplets</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <Card className="my-8 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">
                    Analysez les critères automatiquement
                  </h3>
                  <p className="mb-4 text-violet-100">
                    Notre IA extrait automatiquement les critères d'attribution et leur pondération depuis le RC. Identifiez en 30 secondes les points clés sur lesquels concentrer vos efforts.
                  </p>
                  <Link to="/analyse-dce">
                    <Button className="bg-white text-violet-700 hover:bg-violet-50">
                      Analyser un DCE gratuitement
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Conclusion
              </h2>

              <p>
                Les critères d'attribution sont la clé pour construire une offre gagnante. En comprenant comment les acheteurs évaluent les réponses, vous pouvez :
              </p>

              <ul className="space-y-2 my-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>Concentrer vos efforts sur les critères à forte pondération</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>Structurer votre mémoire technique selon les sous-critères</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>Maximiser votre note finale et vos chances de remporter le marché</span>
                </li>
              </ul>

            </div>

            {/* Aller plus loin - Pillar Pages */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Aller plus loin</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/veille-marches-publics" className="group p-4 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors">
                  <span className="font-semibold text-violet-700 group-hover:text-violet-800">Découvrir la veille marchés publics</span>
                  <p className="text-sm text-slate-600 mt-1">Surveillez les opportunités adaptées à votre entreprise</p>
                </Link>
                <Link to="/trouver-appels-offres" className="group p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
                  <span className="font-semibold text-indigo-700 group-hover:text-indigo-800">Apprendre à trouver des appels d'offres</span>
                  <p className="text-sm text-slate-600 mt-1">Sources, méthodes et bonnes pratiques</p>
                </Link>
                <Link to="/analyse-dce" className="group p-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                  <span className="font-semibold text-emerald-700 group-hover:text-emerald-800">Comprendre l'analyse d'un DCE</span>
                  <p className="text-sm text-slate-600 mt-1">Méthode IA pour extraire les informations clés</p>
                </Link>
                <Link to="/plateforme-appels-offres" className="group p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                  <span className="font-semibold text-orange-700 group-hover:text-orange-800">Découvrir la plateforme DCE Express</span>
                  <p className="text-sm text-slate-600 mt-1">Toutes les fonctionnalités pour répondre aux marchés</p>
                </Link>
              </div>
            </div>

            {/* Internal Links */}
            <InternalLinks 
              links={[
                { label: 'Comment répondre à un appel d\'offres', href: '/blog/comment-repondre-appel-offres' },
                { label: 'RC vs CCTP vs CCAP', href: '/blog/rc-vs-cctp-vs-ccap' },
                { label: 'Comment rédiger un mémoire technique', href: '/blog/comment-rediger-memoire-technique' },
                { label: 'Répondre aux marchés publics', href: '/repondre-marches-publics' }
              ]}
            />

            {/* Author */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">L'équipe Mon DCE Express</p>
                  <p className="text-sm text-slate-500">Experts en marchés publics et analyse DCE</p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
